import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Bookings.css';
import './ServiceCard.css';
import { apiRequest } from './utils';
import translations from './lang';

const Bookings = () => {
  const [myBookings, setMyBookings] = useState([]);
  const [expandedBookingId, setExpandedBookingId] = useState(null);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [mapAddress, setMapAddress] = useState('');
  const [vacationsByUser, setVacationsByUser] = useState({});

  const navigate = useNavigate();
  const language = localStorage.getItem('language') || 'ro';
  const t = translations[language];

  // DD.MM.YYYY
  const fmtDMY = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  };
  // HH:MM
  const fmtHM = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  };

  const pickRelevantVacation = (vacations, serviceId) => {
    if (!Array.isArray(vacations) || vacations.length === 0) return null;
    const now = new Date();
    const relevant = vacations.filter(v => v && (v.service === null || v.service === serviceId));
    if (relevant.length === 0) return null;

    const active = relevant.find(v => {
      const s = new Date(v.start_at);
      const e = new Date(v.end_at);
      return s <= now && now <= e;
    });
    if (active) return active;

    const future = relevant
      .filter(v => new Date(v.start_at) > now)
      .sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
    return future[0] || null;
  };

  useEffect(() => {
    fetchMyBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const providerIds = [...new Set(myBookings.map(b => b?.user_data?.id).filter(Boolean))];
    const missing = providerIds.filter(id => vacationsByUser[id] === undefined);
    if (missing.length === 0) return;

    (async () => {
      try {
        const results = await Promise.all(
          missing.map(async (pid) => {
            const res = await fetch(`${process.env.REACT_APP_API}/api/vacations-by-user-id/${pid}/`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                'Accept-Language': language,
              },
            });
            const data = await res.json().catch(() => ({ data: [] }));
            return [pid, Array.isArray(data?.data) ? data.data : []];
          })
        );
        setVacationsByUser(prev => {
          const next = { ...prev };
          results.forEach(([pid, list]) => { next[pid] = list; });
          return next;
        });
      } catch (e) {
        console.error('Vacations preload error:', e);
      }
    })();
  }, [myBookings, language]);

  const fetchMyBookings = () => {
    apiRequest(`${process.env.REACT_APP_API}/api/my-bookings/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        'Accept-Language': language,
      },
    })
      .then(response => response.json())
      .then(data => {
        const filteredBookings = (Array.isArray(data) ? data : []).filter(
          booking => booking.bookings && booking.bookings.length > 0
        );
        setMyBookings(filteredBookings);
      })
      .catch(error => {
        console.error('Error fetching my bookings:', error);
        setMyBookings([]); 
      });
  };

  const toggleDetails = (id) => {
    setExpandedBookingId(expandedBookingId === id ? null : id);
  };

  const handleBookingClick = (userId, serviceId, bookingDate) => {
    navigate(`/booking-details/${userId}/${serviceId}/${bookingDate}`);
  };

  const getStatusDisplay = (booking) => {
    if (!booking?.bookings?.length) return { symbol: '', text: '', className: '' };
    const status = booking.bookings[0].status?.toLowerCase();
    switch (status) {
      case 'confirmed': return { symbol: '✅', text: t.status_confirmed, className: 'status-confirmed' };
      case 'pending':   return { symbol: '⚠️', text: t.status_pending,   className: 'status-pending' };
      case 'rejected':  return { symbol: '❌', text: t.status_rejected,  className: 'status-rejected' };
      default:          return { symbol: '',   text: '',                 className: '' };
    }
  };

  const openMapModal = (addr) => {
    if (!addr) return;
    setMapAddress(addr);
    setMapModalOpen(true);
  };

  return (
    <div className="bookings-container">
      <h2>{t.bookings}</h2>
      <div className="service-card-grid">
        {myBookings.length === 0 ? (
          <p>{t.no_bookings}</p>
        ) : (
          myBookings.map((booking) => {
            const bookingDate = new Date(booking.start_time).toISOString().split('T')[0]; // URL-hez marad ISO
            const providerId = booking.user_data ? booking.user_data.id : null;
            const serviceId = booking.service;
            const status = getStatusDisplay(booking);

            const serviceAddress =
              booking.service_address ||
              booking.address ||
              booking.service?.address ||
              booking.service_data?.address ||
              '';

            // szabadság jelvény
            const vacList = providerId ? vacationsByUser[providerId] : undefined;
            const vac = vacList ? pickRelevantVacation(vacList, serviceId) : null;
            const showVac = Boolean(vac);
            const vacText = showVac
              ? `${t.on_vacation || 'Szabadság'}: ${fmtDMY(vac.start_at)} – ${fmtDMY(vac.end_at)}`
              : '';

            return (
              <div key={booking.id} className={`service-card ${expandedBookingId === booking.id ? 'expanded' : ''}`}>
                <div className={`status-banner ${status.className}`}>
                  {status.symbol} {status.text}
                </div>

                <div
                  className="service-card-background"
                  style={{
                    backgroundImage: booking.service_card_picture
                      ? `url(data:image/jpeg;base64,${booking.service_card_picture})`
                      : `url(${process.env.REACT_APP_API}/media/profile_pictures/default.jpg)`,
                    backgroundSize: 'contain',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    height: '400px',
                    borderRadius: '7px',
                    marginBottom: '7px',
                    position: 'relative',
                    overflow: 'hidden',
                    loading: 'lazy',
                  }}
                >
                  {showVac && (
                    <div
                      className="vacation-badge"
                      style={{
                        position: 'absolute',
                        top: 10,
                        left: 10,
                        zIndex: 2,
                        background: 'rgba(220, 38, 38, 0.92)',
                        color: '#fff',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        fontWeight: 800,
                        fontSize: '14px',
                        boxShadow: '0 6px 16px rgba(0,0,0,0.25)',
                        backdropFilter: 'blur(1px)',
                        letterSpacing: '0.2px',
                      }}
                      title={vacText}
                      aria-label={vacText}
                    >
                      {vacText}
                    </div>
                  )}

                  <div className="service-card-content">
                    <h4>{booking.service_name || 'N/A'}</h4>
                    <p>
                      {t.start_time}: {fmtDMY(booking.start_time)} {fmtHM(booking.start_time)}
                    </p>
                    <p>
                      {t.end_time}: {fmtDMY(booking.end_time)} {fmtHM(booking.end_time)}
                    </p>

                    {serviceAddress && (
                      <p>
                        {t.address || 'Cím'}:{' '}
                        <button
                          type="button"
                          onClick={() => openMapModal(serviceAddress)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#0ea5e9',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            padding: 0,
                            font: 'inherit'
                          }}
                          aria-label="Cím megnyitása térképen"
                          title={t.address || 'Cím'}
                        >
                          {serviceAddress}
                        </button>
                      </p>
                    )}
                  </div>
                </div>

                {expandedBookingId === booking.id && (
                  <div className="service-card-details">
                    <div className="service-card-user-details">
                      <p><strong>{t.provider}:</strong></p>
                      <p>{t.name}: {booking.user_data ? `${booking.user_data.first_name} ${booking.user_data.last_name}` : 'N/A'}</p>
                      {/* <p>Email: {booking.user_data ? booking.user_data.email : 'N/A'}</p>
                      <p>{t.phone_number}: {booking.user_data ? booking.user_data.phone_number : 'N/A'}</p> */}
                    </div>

                    <div className="service-card-package-details">
                      {booking.appointment_packages?.length > 0 && (
                        <div className="service-card-package-table">
                          <p><strong>{t.used_packages}:</strong></p>
                          <table className="package-table">
                            <thead>
                              <tr>
                                <th>{t.name}</th>
                                <th>{t.description}</th>
                                <th>{t.price}</th>
                                <th>{t.duration}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {booking.appointment_packages.map(pkg => {
                                const hours = Math.floor(pkg.service_package_duration);
                                const minutes = Math.round((pkg.service_package_duration % 1) * 60);
                                return (
                                  <tr key={pkg.id}>
                                    <td>{pkg.service_package_name}</td>
                                    <td>{pkg.service_package_description}</td>
                                    <td>{pkg.service_package_price} RON</td>
                                    <td>{hours} {t.hours} {minutes} {t.minutes}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="service-card-button-container">
                  <button onClick={() => toggleDetails(booking.id)}>
                    {expandedBookingId === booking.id ? t.close : t.details}
                  </button>
                  <button onClick={() => handleBookingClick(providerId, serviceId, bookingDate)}>
                    {t.open_in_calendar}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {mapModalOpen && (
        <div className="modal" role="dialog" aria-modal="true" onClick={() => setMapModalOpen(false)}>
          <div
            className="modal-content"
            style={{ maxWidth: 900, width: '95%', height: '70vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <h4 style={{ margin: 0 }}>{t.address || 'Cím'}</h4>
              <button className="close-icon" onClick={() => setMapModalOpen(false)} aria-label="Bezárás">×</button>
            </div>

            <div style={{ margin: '8px 0 12px' }}>{mapAddress}</div>

            <iframe
              title="Google Maps"
              width="100%"
              height="100%"
              src={`https://www.google.com/maps?q=${encodeURIComponent(mapAddress)}&hl=${encodeURIComponent(language)}&z=15&output=embed`}
              loading="lazy"
              style={{ border: 0, borderRadius: 8, width: '100%', height: 'calc(100% - 60px)' }}
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />

            <div style={{ marginTop: 8, textAlign: 'right' }}>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapAddress)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t.open_in_maps || 'Megnyitás a Google Térképen'}
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bookings;
